import { Observable } from 'rxjs';
import { of } from 'rxjs';
import { Injectable, Input, Inject } from '@angular/core';
import { ApiBrokerService, AppSessionService, OpCo, FeatureFlagService } from '@eucoms/common-components';
import { BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AppConfigService } from '@eucoms/common-components';
import { Title } from '@angular/platform-browser';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { GoogleAnalyticsService } from './google-analytics-service';
import { constants } from '@app/constants';
import { URLConfig } from '@config/url-config';
import { TimeFormatService } from '@app/tracker/services/time-format.service';
import { UserQueryResponse } from '@app/tracker/interfaces/outage-tracker.interface';

export interface AccountData {
    accountNumber: string;
    address: string;
    status: string;
}

@Injectable()
export class SharedService {
    api: string;
    opco = this.appConfigService.config.opCo;
    firstNavigationComplete: boolean;
    anonBaseEndPoint = '/anon/{{opCo}}/outage';
    authBaseEndPoint = '/auth/accounts';
    stormFlag: boolean;
    param: any;
    token: string;
    premiseNumber;
    readonly routeThroughExceptions = /fallbackReport|tracker|outageTracker|outageMap/;
    public storedAccountData = new BehaviorSubject<any>({});
    public storedOutageData = new BehaviorSubject<any>([]);
    public storedAnonOutageData = new BehaviorSubject<any>([]);
    public storedPecoAccountData = new BehaviorSubject<any>([]);
    public storedPingData = new BehaviorSubject<any>({});
    USE_PING_MOCK_DATA = true;
    staticData = {};
    handleAcct = {};
    isAmiFlag: boolean;
    isEnabledDynamicHeader: boolean;
    constructor(
        private apiBroker: ApiBrokerService,
        private appConfigService: AppConfigService,
        private titleService: Title,
        private router: Router,
        private appSessionService: AppSessionService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private featureFlagService: FeatureFlagService,
        private timeFormatService: TimeFormatService,
        @Inject('windowObject') private win: Window,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const customEvent = '';
                const route = event.url.replace(/^\/?([^/]+)/, '$1');
                // Google Analytics for all app routes
                if (
                  !this.loadEUDSModule(route) &&
                  this.isSubscribedToGAPageNavigated
                ) {
                  this.googleAnalyticsService.pageNavigated(
                    event.url,
                    this.isAuthenticated,
                    customEvent
                  );
                }
            }
        });
    }

    gaService(customEvent: string) {
        this.googleAnalyticsService.pageNavigated(this.router.url, this.isAuthenticated, customEvent);
    }

    get isSubscribedToGAPageNavigated() {
        return constants.isSubscribeGAPageNavigated;
    }

    get isAuthenticated(): boolean {
        return this.appSessionService.session.token ? true : false;
    }

    get getEnrollOutageUrl(): string {
        if (this.isAuthenticated && OpCo.BGE === this.opco) {
            return URLConfig['bgePreferenceCenterUrl'];
        } else if (this.isAuthenticated && [OpCo.PEPCO, OpCo.DPL, OpCo.PECO, OpCo.ACE].includes(this.opco) ) {
            return URLConfig['preferenceCenterUrl'];
        } else {
            return URLConfig['enrollOutageUrl'];
        }
    }

    doInit() {
        this.token = this.appSessionService.session.token; // sessionStorage.getItem('token');
        this.premiseNumber = this.appSessionService.session.premiseNumber; // sessionStorage.getItem('premiseNumber');
        this.stormFlag = false;
    }

    summarizeUrl(url: string) {

        if (this.stormFlag && (this.opco === 'PECO' || this.opco === 'PEPCO' || this.opco === 'DPL' || this.opco === 'ComEd')) {
            console.log(url);
            url = this.token ? url + '&summary=true' : url + '?summary=true';
        }

        return url;
    }


    getAccount() {
        this.apiBroker.get(this.authBaseEndPoint)
            .subscribe(
                data => {
                    this.storedAccountData.next(data);
                },
                error => {
                    this.storedAccountData.next(error);
                }
            );
    }

    getAnonOutage(inputType: string, inputValue: string, auid?: any) {
        this.param = {};
        if (inputType === 'accountNumber') {
            this.param['account_number'] = inputValue;
        } else {
            this.param['phone'] = inputValue;
        }
        if (auid && auid !== 'undefined') {
            this.param['auid'] = auid;
        }
        return this.apiBroker.post<any>(this.summarizeUrl(this.anonBaseEndPoint + '/query'), this.param)
        .pipe(tap(
            resData => {
                this.onOutageStatusQueryResponse(resData, 'UnAuthenticated');
            }
        ));
    }

    getFlag() {
        const opco = this.opco && this.opco === 'PEPCO' ? 'PEP' : this.opco;
        const uri = '/anon/{{opco}}/config/features';
        return this.apiBroker.get(uri);
    }

    get checkAmiEnabledOpco(): boolean {
        const opco = this.appConfigService.config.opCo;
        return opco === 'ACE' ? true : false;
    }

    getAccountNumberDetails(accountNumber: number) {
        const api = '/anon/{{opCo}}/outage/query';
        this.param = { account_number: accountNumber };
        return this.apiBroker.post<any>(api, this.param);
    }

    getPreCheck(accountNumber: any, phone: any, premiseId: any, auid?: any) {
        this.doInit();
        this.param = {};
        if (accountNumber && accountNumber !== 'undefined') {
            this.param['accountNumber'] = accountNumber;
        }
        if (premiseId && premiseId !== 'undefined') {
            this.param['premiseId'] = premiseId;
        }
        if (phone && phone !== 'undefined') {
            this.param['phone'] = this.convertPhoneNumber(phone);
        }
        if (auid && auid !== 'undefined') {
            this.param['auid'] = auid;
        }

        if (this.token) {
            return this.apiBroker.get(this.authBaseEndPoint + '/' + accountNumber + '/premises/' +
                this.premiseNumber + '/outage/precheck', this.param);
        } else {
            return this.apiBroker.post(this.anonBaseEndPoint + '/precheck', this.param);
        }
    }

    getPing(accountNumber: any, phone: any, premiseId: any, auid?: any) {
        this.doInit();
        this.param = {};
        if (accountNumber && accountNumber !== 'undefined') {
            this.param['accountNumber'] = accountNumber;
        }
        if (auid && auid !== 'undefined') {
            this.param['auid'] = auid;
        }
        const isUsePingMock = this.appConfigService.config.isUsePingMock;
        if (!isUsePingMock) {
            if (this.token) {
                return this.apiBroker.get(this.authBaseEndPoint + '/' + accountNumber + '/premises/' +
                    this.premiseNumber + '/outage/ping', this.param);
            } else {
                return this.apiBroker.post(this.anonBaseEndPoint + '/ping', this.param);
            }
        } else {
            return this.getPingMock();
        }
    }

    getPingMock() {
        return of({ data: { meterInfo: { pingResult: true } } });
    }

    getOutages(account: number, type: string) {
        let params = '?payments=false&budgetBilling=false&programs=false';
        params += this.appConfigService.config.opCo === 'BGE' ? '&includeMDM=false' : '';
        return this.apiBroker.get(this.summarizeUrl(this.authBaseEndPoint + '/' + account + (type ? '/' : '')
            + type + params))
            .pipe(map(resData => {
                return resData['success'] ? resData['data'] : resData;
            }), tap(resData => {
                this.onOutageStatusQueryResponse(resData, 'Authenicated');
            }));
    }

    private onOutageStatusQueryResponse(resData, AuthenticationStatus) {
        let outageData = resData['success'] ? resData['data'] : resData;
        outageData = outageData[0];
        if (outageData) {
            sessionStorage.setItem('m_PremiseNumber', outageData?.premiseNumber);
            sessionStorage.setItem('m_ContactNumber', outageData?.contactHomeNumber);
            sessionStorage.setItem('m_OutageReported', outageData?.outageReported);
            sessionStorage.setItem('m_AuthenticationStatus', AuthenticationStatus);
        }
    }

    submitAnswer(token: any, accountNumber: any, outageIssue: any, unusual: any, unusualSpecify: any, neighbor: any,
        construction: any, phone: any, ext: any, locationId: any, auid?: any) {
        let api: string;
        api = token ? this.authBaseEndPoint + '/' + accountNumber + '/outage' : '/anon/{{opCo}}/outage';
        this.param = {
            account_number: accountNumber,
            outage_issue: outageIssue,
            unusual: unusual,
            unusual_specify: unusualSpecify,
            neighbor: neighbor,
            construction: construction,
            phone: phone,
            ext: ext,
            location_id: locationId
        };
        if (auid && auid !== 'undefined') {
            this.param['auid'] = auid;
        }
        return this.apiBroker.post(api, this.param);
    }

    convertPhoneNumber(phoneNumber: any) {
        // eslint-disable-next-line max-len
        phoneNumber = phoneNumber && phoneNumber !== 'undefined' ? phoneNumber.replace('(', '').replace(')', '').replace('-', '').replace(' ', '') : '';
        return phoneNumber;
    }

    formatPhoneNumber(number: string) {
        let formattedNumber = number.replace('(', '').replace(')', '').replace('-', '').replace(' ', '');
        formattedNumber = '(' + formattedNumber.substr(0, 3) + ') ' + formattedNumber.substr(3, 3) + '-' + formattedNumber.substr(6, 4);
        return formattedNumber;
    }

    get isEnableDynamicHeader(): boolean {
        let isEnabled = false;
        const opco = this.appConfigService.config.opCo;
        const isUserAuthenticated = this.appSessionService.isUserAuthenticated();

        if (opco === 'DPL' || opco === 'PEPCO' || opco === 'BGE' || opco === 'PECO' || opco === 'ACE') {
            isEnabled = true;
        }
        return isEnabled;
    }

    getCommonHeaderTitle(): string {
        const source: string = this.appSessionService.session.source;
        let headerTitle: string = this.appConfigService.config.reportOutageTitle;
        if (source.toLowerCase() === 'checkoutage' && this.isEnableDynamicHeader) {
            headerTitle = 'Check My Outage Status';
        }
        return headerTitle;
    }

    getHtmlTitle(): string {
        const opcoTitle = `${this.getCommonHeaderTitle()} | ${this.appConfigService.config.companyDescription}`;
        return opcoTitle;
    }

    getContactNo(): string {
        const opcoContactNo = this.appConfigService.config.contactNo;
        return opcoContactNo;
    }

    getInfoMessages(): string[] {
        const infoMessages = this.appConfigService.config.infoMessages;
        return infoMessages;
    }

    handleAccounts(response) {
        const model = {};
        const code = response.meta ? response.meta.code : '';
        switch (code) {
            case 'FN-ACCOUNT-NOPAY': {
                model['isCutOutNonPay'] = 'Y';
                model['flagNoPay'] = 'Y';
                break;
            }
            case 'FN-ACCOUNT-INACTIVE': {
                model['flagInactive'] = 'Y';
                break;
            }
            case 'FN-ACCOUNT-FINALED': {
                model['flagFinaled'] = true;
                model['outageReported'] = this.staticData['AccountValidation']['ReportAnOutage'].FinaledAccountError;
                break;
            }
            case 'FN-CUT-AT-POLE': {
                model['flagCutAtPole'] = 'Y';
                model['isCutOutNonPay'] = 'N';
                model['flagNoPay'] = 'N';
                break;
            }
            case 'FN-ACCT-NOTFOUND': {
                model['showInvalidAccountMessage'] = true;
                break;
            }
            case 'FN-ACCT-PROTECTED': {
                model['isPasswordProtected'] = true;
                break;
            }
            default: { }
        }
        return model;
    }

    handleAccountSuccess(data) {
        this.doInit();
        const updateData = this.token ? data : data.data;
        updateData.map(function (i) {
            if (!i.flagGasOnly) {
                i.flagNoPay = 'N';
                i.flagFinaled = false;
                i.flagCutAtPole = 'N';
                i.flagInactive = 'N';
            }
        });
    }

    handleAccountUnSuccess(data) {
        data['OutageInfo'] = {};
        const handleAcct = this.handleAccounts(data);
        data['OutageInfo'].FlagNoPay = this.opco ? handleAcct['isCutOutNonPay'] ? 'Y' : 'N' : handleAcct['flagNoPay'];
        data['OutageInfo'].flagFinaled = false;
        data['OutageInfo'].flagCutAtPole = handleAcct['flagCutAtPole'];
        data['OutageInfo'].flagInactive = handleAcct['flagInactive'];
        data['OutageInfo'].showInvalidAccountMessage = handleAcct['showInvalidAccountMessage'];
    }

    isPageInIframe(): boolean {
        let isInIframe = false;
        if (this.win.location !== this.win.parent.location) {
            isInIframe = true;
        }
        return isInIframe;
    }

    /**
    * function to show/hide outage tracker link
    * @method getIsShowingTrackerLink
    */
    getIsShowingTrackerLink(): boolean {
        return this.opco === OpCo.BGE;
    }

    // show the registration button component if feature flag is on
    showRegistrationButton(): boolean {
        let isShowingRegistrationButton;
        // eslint-disable-next-line max-len
        if (this.featureFlagService.hasFlag('OutageRegistrationButton') && !this.isAuthenticated) {
            isShowingRegistrationButton = true;
        } else {
            isShowingRegistrationButton = false;
        }
        return isShowingRegistrationButton;
    }

    /**
    * function to set sessionStorage parameters used by Medallia surveys for GUEST authentication data
    * @method setMedalliaGuestParams
    * @param UserQueryResponse
    * @returns none, but sets sessionStorage
    */
     public setMedalliaGuestParams(guestUserDetails: UserQueryResponse) {
        sessionStorage.setItem('m_AuthenticationStatus', 'Guest');
        sessionStorage.setItem('m_CustomerID', guestUserDetails?.auid ? guestUserDetails?.auid : guestUserDetails?.maskedAccountNumber);
        sessionStorage.setItem('m_IsSmallCommercialFlag' , 'N/A');
        sessionStorage.setItem('m_IsLargeCommercialFlag' , 'N/A');
    }

    /**
    * function to set sessionStorage parameters used by Medallia surveys for OUTAGE data
    * @method setMedalliaOutageParams
    * @param UserQueryResponse
    * @returns none, but sets sessionStorage
    */
    // ******************************************************************************** */
    // ETR = the ETR, if no ETR is available then "No ETR", Power On "Power On"
    // ETR Type = the ETR type, Power On "Power On"
    // Outage Cause = If there is an outage cause set then "cause code/message",
    //                If no cause has been determined "Cause undetermined", Power on "Power On"
    // Outage ID = outage ID, Power on "Power On"
    // Tracker Status = Outage Reported, Crew Assigned, Crew Enroute, Crew Onsite, Power Restored, Power On
    // Outage Valid = True for Outage Reported, Crew Assigned, Crew Enroute, Crew Onsite, Power Restored and False for Power On
    // ******************************************************************************** */
    public setMedalliaOutageParams(outageDetails: any) {
        sessionStorage.setItem('m_isOutageValid' , String(outageDetails?.isOutageValid?.toString()));
        sessionStorage.setItem('m_trackerStatus' , outageDetails?.trackerStatus ? String(outageDetails?.trackerStatus) : 'Power On');

        if (outageDetails?.isOutageValid === true) { // outage
            sessionStorage.setItem('m_etr', outageDetails?.etr ? this.timeFormatService.returnFormattedDate(outageDetails?.etr) : 'No ETR');
            sessionStorage.setItem('m_etrType' , outageDetails?.etrType);
            sessionStorage.setItem('m_outageCause', outageDetails?.causeCodeDisplayValue ? outageDetails?.causeCodeDisplayValue : 'Cause undetermined');
            sessionStorage.setItem('m_outageID', outageDetails?.outageID);
        } else {  // power on
            sessionStorage.setItem('m_etr', 'Power On');
            sessionStorage.setItem('m_etrType' , 'Power On');
            sessionStorage.setItem('m_outageCause', 'Power On');
            sessionStorage.setItem('m_outageID', 'Power On');
        }
    }

    loadEUDSModule(route: string): boolean {
        const eudsPages = ['outageTracker'];
        return eudsPages.some(url => route && route.includes(url));
    }
}
